import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ModerationStatuses, ModerationTransitions } from '../../../../../../models';

import { Button, Form, Icon, Message, ToastContext, ToggleContainer } from '@weezevent/nacre';

import Styles from '../transitionDetails.module.css';

export default function SettingsStep({ form, setForm, onCardDone, organization, flowId, transitionExists }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [statuses, loadingStatuses] = ModerationStatuses.useApiModel(
        {
            organizationId: organization.id,
            flowId
        },
        [organization, flowId]
    );

    const statusesOptions = React.useMemo(() => {
        if (!statuses || loadingStatuses) {
            return [];
        }
        return statuses.map(status => ({ key: status.id, value: status.id, text: status.name }));
    }, [statuses, loadingStatuses, flowId]);

    const handleSubmit = React.useCallback(
        (isQuitting = false) => {
            let transition = new ModerationTransitions({ ...form });
            transition.actions = form.actions.map(ac => ac.id);
            transition.conditions = form.conditions.map(ac => ac.id);
            setIsSubmitting(true);

            transition
                .save({ organizationId: organization.id, flowId })
                .then(({ response }) => {
                    toast.success(t('ticket.moderation.statusesTransitions.transition.steps.settings.messageSuccessSave'));
                    if (isQuitting) {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
                    } else {
                        onCardDone(true);
                        setForm('id', response.id);
                    }
                })
                .catch(() => {
                    toast.error(t('ticket.moderation.statusesTransitions.transition.steps.settings.messageErrorSave'));
                    setIsSubmitting(false);
                });
        },
        [form, organization.id, flowId]
    );

    const hasStatusFieldsFilled = React.useMemo(() => Boolean(form.source_status && form.destination_status), [form.source_status, form.destination_status]);

    return (
        <div>
            <Form.Field>
                <Form.Label label={t('ticket.moderation.statusesTransitions.transition.steps.settings.nameLabel')} />
                <Form.Input
                    name="name"
                    placeholder={t('ticket.moderation.statusesTransitions.transition.steps.settings.nameLabel')}
                    onChange={({ target: { value } }) => setForm('name', value)}
                    value={form.name}
                />
            </Form.Field>
            <div className={Styles['transition-status']}>
                <Form.Field className={Styles['transition-status-field']}>
                    <Form.Label label={t('ticket.moderation.statusesTransitions.transition.steps.settings.sourceStatusLabel')} />
                    <Form.Select
                        name="source_status"
                        options={statusesOptions}
                        placeholder={t('ticket.common.action.select')}
                        useNew
                        onChange={(_, { key }) => setForm('source_status', key)}
                        value={form.source_status}
                    />
                </Form.Field>
                <span className={Styles['status-separator']}>
                    <Icon name="arrow-right" />
                </span>
                <Form.Field className={Styles['transition-status-field']}>
                    <Form.Label label={t('ticket.moderation.statusesTransitions.transition.steps.settings.destinationStatusLabel')} />
                    <Form.Select
                        name="destination_status"
                        options={statusesOptions}
                        placeholder={t('ticket.common.action.select')}
                        useNew
                        onChange={(_, { key }) => setForm('destination_status', key)}
                        value={form.destination_status}
                    />
                </Form.Field>
            </div>
            <Message
                title={t('ticket.moderation.statusesTransitions.transition.steps.settings.sameStatusTitle')}
                type="default"
                visible={hasStatusFieldsFilled && form.source_status === form.destination_status}
            >
                {t('ticket.moderation.statusesTransitions.transition.steps.settings.sameStatusDesc')}
            </Message>
            <ToggleContainer
                title={t('ticket.moderation.statusesTransitions.transition.steps.settings.autoTransition.label')}
                subtitle={t('ticket.moderation.statusesTransitions.transition.steps.settings.autoTransition.subLabel')}
                onChange={({ target: { checked } }) => setForm('is_automatic', checked)}
                toggle={form.is_automatic}
            />
            {form.is_automatic && (
                <Message title={t('ticket.moderation.statusesTransitions.transition.steps.settings.autoTransition.message.title')} type="warning" visible>
                    {t('ticket.moderation.statusesTransitions.transition.steps.settings.autoTransition.message.text')}
                </Message>
            )}
            {!transitionExists && (
                <Button.Group>
                    <Button
                        primary
                        label={t('ticket.moderation.statusesTransitions.transition.steps.settings.saveAndContinue')}
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !hasStatusFieldsFilled}
                    />
                    <Button
                        primary
                        inverted
                        label={t(`ticket.moderation.statusesTransitions.transition.steps.settings.saveAndQuit`)}
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitting || !hasStatusFieldsFilled}
                    />
                </Button.Group>
            )}
        </div>
    );
}
